import React, { useState, useEffect } from 'react';
import { Box, AppBar, Toolbar, IconButton, Typography, alpha, CircularProgress } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import ConstituencyDashboard from './components/ConstituencyDashboard';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import LandingPage from './components/LandingPage';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import API from './pages/API';
import About from './pages/About';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import sessionManager from './utils/sessionManager';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState(null); // null = loading, 'landing', 'login', 'dashboard'
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // Check for saved user session on app startup
    const initializeApp = async () => {
      try {
        // Add a minimum loading time to prevent flash
        const minLoadingTime = new Promise(resolve => setTimeout(resolve, 600));
        
        const savedUser = sessionManager.loadSession();
        if (savedUser) {
          setUser(savedUser);
          setCurrentView('dashboard');
        } else {
          setCurrentView('landing');
        }

        // Wait for minimum loading time
        await minLoadingTime;
      } catch (error) {
        console.error('Error loading saved user session:', error);
        setCurrentView('landing');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeApp();
  }, []);

  const handleSignInClick = () => {
    setCurrentView('login');
  };

  const handleNavigate = (view) => {
    setCurrentView(view);
  };

  // Helper to render dashboard based on currentView
  const renderDashboard = () => {
    if (currentView === 'dashboard') {
      return <ConstituencyDashboard />;
    }
    if (currentView === 'candidate') {
      const CandidateDashboard = require('./components/CandidateDashboard').default;
      return <CandidateDashboard />;
    }
    if (currentView === 'party') {
      const PartyDashboard = require('./components/PartyDashboard').default;
      return <PartyDashboard />;
    }
    return null;
  };
  const handleLogin = (userData) => {
    sessionManager.saveSession(userData);
    setUser(userData);
    setCurrentView('dashboard');
  };

  const handleLogout = () => {
    sessionManager.clearSession();
    setUser(null);
    setCurrentView('landing');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Show loading spinner during initialization
  if (isInitializing) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1
          }}
        />
        
        <Box textAlign="center" sx={{ zIndex: 1 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              background: 'linear-gradient(45deg, #ffffff 30%, #f0f0f0 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            CivIQ
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              opacity: 0.9,
              fontWeight: 300 
            }}
          >
            Constituency Intelligence Platform
          </Typography>
          <CircularProgress 
            size={50} 
            sx={{ 
              color: 'white',
              mb: 2,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
          />
          <Typography 
            variant="body1" 
            sx={{ 
              opacity: 0.8,
              fontWeight: 300
            }}
          >
            Initializing Dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show landing page for non-authenticated users
  if (currentView === 'landing') {
    return <LandingPage onSignInClick={handleSignInClick} onNavigate={handleNavigate} />;
  }

  // Show login page
  if (currentView === 'login') {
    return <Login onLogin={handleLogin} onBackToLanding={() => setCurrentView('landing')} />;
  }

  // Show footer pages
  if (currentView === 'features') {
    return <Features onNavigate={handleNavigate} />;
  }
  
  if (currentView === 'pricing') {
    return <Pricing onNavigate={handleNavigate} />;
  }
  
  if (currentView === 'api') {
    return <API onNavigate={handleNavigate} />;
  }
  
  if (currentView === 'about') {
    return <About onNavigate={handleNavigate} />;
  }
  
  if (currentView === 'contact') {
    return <Contact onNavigate={handleNavigate} />;
  }
  
  if (currentView === 'privacy') {
    return <PrivacyPolicy onNavigate={handleNavigate} />;
  }

  return (
    <div className="App">
      {/* Top Navigation Bar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          color: 'primary.main'
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleSidebar}
            sx={{
              mr: 2,
              backgroundColor: alpha('#1976d2', 0.1),
              '&:hover': {
                backgroundColor: alpha('#1976d2', 0.2),
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            CivIQ Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Welcome, {user.name}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Sidebar 
        open={sidebarOpen}
        onClose={closeSidebar}
        user={user}
        onLogout={handleLogout}
        onNavigate={handleNavigate}
      />

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          pt: 8, // Account for AppBar height
          minHeight: '100vh'
        }}
      >
        {renderDashboard()}
      </Box>
    </div>
  );
}

export default App;
